import { DI_TOKENS, resolve } from '@src/di/container';
import {
  getExternalUploadsBucketPathKey,
  renameFile,
} from '@src/services/upload';
import { LogManager } from '@src/utility/Logger';
import { v4 as uuid } from 'uuid';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { UserIdentityStore } from '@src/stores-v2/UserIdentityStore';
import type { Showcase } from '@src/types.ts/story';
// TODO: pollStoryUpdates currently unused externally; query constant left out pending centralization

// Temporary lightweight types (mirroring hook). Ideally import from central types.
export type StoryVideoMusicStrategy = 'keep' | 'process';

export interface StoryCreationFlags {
  storyManagerEnableAwsSdkUploads: boolean;
  enableLargeFileSizeUpload: boolean;
  enableNoLimitFileSizeUpload: boolean;
}

export interface CreateBlankStoryParams {
  storyType: 'raw' | 'produced' | null;
  storyTellerName: string;
  storyTellerTitle: string;
  organization?: Showcase;
  userData?: { id: string; name: string; email: string; type: string } | null;
}

export interface StartProcessingParams {
  storyId: string;
  storyName: string;
  storyType: 'raw' | 'produced';
  storyVideoUrl?: string | null;
  isUrlUpload: boolean;
  isMultiFileUpload: boolean;
  multiFileVideoUrls?: string[] | null;
  storyVideoFile?: File | null;
  organizationId?: string;
  videoMusicStrategy: StoryVideoMusicStrategy;
  tracker?: {
    transactionId: string | null;
    analyticsContext: Record<string, unknown>;
    analyticsLog: (...args: any[]) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
}

export interface VideoUploadProgressCallbacks {
  onProgress: (percent: number, done?: boolean) => void;
  onUploaded: () => void;
  onError: (err: unknown) => void;
}

export interface MultiFileUploadResult {
  uploadedUrls: string[];
}

export interface StoryCreationServiceDeps {
  videoCreator: VideoCreatorStore | IVideoCreatorStore;
  datoClientStore: DatoClientStore;
  userIdentity: UserIdentityStore;
}

const logger = LogManager.getLogger('service:StoryCreationService');

export class StoryCreationService {
  private videoCreator: VideoCreatorStore | IVideoCreatorStore;
  private datoClientStore: DatoClientStore;
  private userIdentity: UserIdentityStore;

  constructor(deps: StoryCreationServiceDeps) {
    this.videoCreator = deps.videoCreator;
    this.datoClientStore = deps.datoClientStore;
    this.userIdentity = deps.userIdentity;
  }

  formatCurrentUserData() {
    const userData = this.userIdentity.getUserIdentity?.();
    if (!userData) return null;
    const { id, name, email, type } = userData;
    return { id, name, email, type };
  }

  async createBlankStory(params: CreateBlankStoryParams) {
    logger.info('createBlankStory', {
      type: params.storyType,
    });

    const blank = await this.videoCreator.createStory({
      title: `user-upload-${uuid()}`,
      storyType: params.storyType || undefined,
      storyTeller: {
        name: params.storyTellerName,
        id: (
          await this.videoCreator.upsertStoryTeller({
            name: params.storyTellerName,
            title: params.storyTellerTitle,
          })
        ).id,
      },
      // transitional: accessing legacy organization shape on videoCreator

      primaryShowcase: params.organization,
      externalUploadLog: { uploadedBy: params.userData },
      byExternalUser: true,
    });
    return blank;
  }

  async startOldVideoProcessingPipeline(
    storyId: string,
    storyVideoFile?: File | null,
  ) {
    try {
      if (!storyVideoFile) return;
      const fileName = renameFile(storyVideoFile, storyId).name;

      const orgId = this.videoCreator.organization?.id;
      const s3Key = getExternalUploadsBucketPathKey(fileName, orgId);
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/api/story/upload-task-old`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storyId, s3Key }),
        },
      );
      if (!resp.ok) throw new Error(`old-processing-not-ok ${resp.status}`);
    } catch (err) {
      logger.warn('old-processing-pipeline-error suppressed', { err });
    }
  }

  async startProcessingUploadedVideo(params: StartProcessingParams) {
    const {
      storyId,
      storyName,
      storyType,
      isUrlUpload,
      isMultiFileUpload,
      multiFileVideoUrls,
      storyVideoUrl,
      storyVideoFile,
      organizationId,
      videoMusicStrategy,
      tracker,
    } = params;

    try {
      const requestBody: Record<string, unknown> = {
        transactionId: tracker?.transactionId,
        analyticsContext: {
          ...(tracker?.analyticsContext || {}),
          story: { id: storyId, name: storyName },
        },
        storyId,
        showcaseId: organizationId,
        userData: this.formatCurrentUserData(),
        audioProcessPreset:
          videoMusicStrategy === 'process'
            ? storyType === 'produced'
              ? 'REMOVE_MUSIC'
              : 'CORRECT_AUDIO'
            : undefined,
        transcriptionProviders: ['rev', 'aws', 'assembly'],
      };

      if (isUrlUpload && storyVideoUrl) {
        requestBody.sourceUrl = storyVideoUrl;
        requestBody.s3Key = `sessions/${storyId}/videos/original.mp4`;
      } else if (isMultiFileUpload && multiFileVideoUrls) {
        requestBody.videoUrls = multiFileVideoUrls;
        requestBody.isMultiFileConcat = true;
      } else if (storyVideoFile) {
        requestBody.s3Key = getExternalUploadsBucketPathKey(
          renameFile(storyVideoFile, storyId).name,
          organizationId,
        );
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/story/upload-task`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        },
      );
      if (!response.ok) throw new Error(`processing-not-ok ${response.status}`);
    } catch (error) {
      tracker?.analyticsLog?.('error', 'Error calling video processing API', {
        error,
      });
      throw error;
    }
  }

  async uploadMultiFiles(
    files: File[],
    storyId: string,
    organizationId: string | undefined,
    clerkToken: string | undefined,
    callbacks: VideoUploadProgressCallbacks,
    preferAws: boolean,
    onMechanismResolved?: (m: 'worker' | 'main-thread') => void,
  ): Promise<MultiFileUploadResult> {
    const uploadService = resolve(DI_TOKENS.UPLOAD_SERVICE);
    if (!uploadService) throw new Error('UploadService not initialized');
    // Upload all files in parallel tracking aggregate progress
    const progressMap = new Map<number, number>();
    files.forEach((_, idx) => progressMap.set(idx, 0));

    const computeOverall = () => {
      const total = Array.from(progressMap.values()).reduce((s, v) => s + v, 0);
      return total / files.length; // raw 0-100 overall percent
    };

    const uploadPromises = files.map(async (file, i) => {
      const key = getExternalUploadsBucketPathKey(
        `${storyId}_part_${i}`,
        organizationId,
      );
      const org = this.videoCreator.organization?.organization;
      await uploadService.uploadFileToS3ExternalUploads(file, org, {
        onUploadProgress: (p: number, done?: boolean) => {
          progressMap.set(i, p);
          callbacks.onProgress(Math.min(100, computeOverall()), done);
        },
        preferAwsSdk: preferAws,
        ...(clerkToken ? { authToken: clerkToken } : {}),
        onResolvedMechanism: m => {
          try {
            onMechanismResolved?.(m);
          } catch {
            /* ignore */
          }
        },
      });
      return `https://${process.env.REACT_APP_EXTERNAL_UPLOADS_BUCKET}.s3.amazonaws.com/${key}`; // heuristic
    });

    const uploadedUrls = await Promise.all(uploadPromises);
    callbacks.onProgress(100, true);
    callbacks.onUploaded();
    return { uploadedUrls };
  }

  async uploadSingleFile(
    file: File,
    storyId: string,
    organization: string | null | undefined, // transitional narrowed
    clerkToken: string | undefined,
    callbacks: VideoUploadProgressCallbacks,
    preferAws: boolean,
    onMechanismResolved?: (m: 'worker' | 'main-thread') => void,
  ): Promise<void> {
    const uploadService = resolve(DI_TOKENS.UPLOAD_SERVICE);
    if (!uploadService) throw new Error('UploadService not initialized');
    const renamed = renameFile(file, storyId);
    await uploadService.uploadFileToS3ExternalUploads(renamed, organization, {
      onUploadProgress: (progress: number, done?: boolean) => {
        callbacks.onProgress(Math.min(100, progress), done);
        if (done) callbacks.onUploaded();
      },
      preferAwsSdk: preferAws,
      ...(clerkToken ? { authToken: clerkToken } : {}),
      onResolvedMechanism: m => {
        try {
          onMechanismResolved?.(m);
        } catch {
          /* ignore */
        }
      },
    });
  }

  // NOTE: pollStoryUpdates removed in initial extraction (hook still owns for now)
}

export type IStoryCreationService = StoryCreationService; // simple alias for DI for now
